import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../lib/theme";
import { Card } from "../components/Card";
import { Btn } from "../components/Btn";
import { Eyebrow } from "../components/Eyebrow";
import { Icon } from "../components/Icon";
import { Skeleton } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { supabase } from "../lib/supabase";
import { logChannelStatus } from "../lib/realtimeStatus";
import { fmtK, fmtKShort } from "../lib/money";
import { timeAgo } from "../lib/dateUtils";
import { BecomeSellerModal } from "../components/BecomeSellerModal";
import { MfaNudgeBanner } from "../components/MfaNudgeBanner";

// Windows are trailing N-day ranges from "now". `days: null` = all time.
const RANGES = [
  { id: "today", label: "Today", days: 1 },
  { id: "7d", label: "7 days", days: 7 },
  { id: "30d", label: "30 days", days: 30 },
  { id: "90d", label: "90 days", days: 90 },
  { id: "all", label: "All time", days: null },
];

function startOfRange(days) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - (days - 1));
  return d;
}

function dayKey(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function weekKey(date) {
  // ISO-ish week key: Monday-anchored.
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0 Sun .. 6 Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return dayKey(d);
}

function monthKey(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Statuses that count as earned revenue (money has landed in escrow/completed).
const EARNING_STATUSES = new Set(["delivered", "completed"]);
// Statuses to exclude from "Orders" KPI — cancelled/refunded aren't real orders.
const VOIDED_STATUSES = new Set(["cancelled", "refunded"]);

export function SellerDashboard({ seller, setActive, openProfileTab, user, onSellerCreated }) {
  const [becomeOpen, setBecomeOpen] = useState(false);
  const [range, setRange] = useState("7d");
  const queryClient = useQueryClient();

  // Cached via TanStack Query — switching tabs returns to the dashboard with
  // an instant render from cache and a quiet background refetch. The realtime
  // channel below invalidates on sub_order changes to keep KPIs live.
  //
  // NOTE: don't use `?? 7` on `matched.days` — `{ days: null }` means all-time
  // and `??` would collapse it to 7, silently breaking the All-time tab.
  const { data, isLoading: loading } = useQuery({
    queryKey: ["seller-dash", seller?.id, range],
    enabled: !!seller?.id,
    staleTime: 30_000,
    queryFn: async () => {
      const matched = RANGES.find((r) => r.id === range) || RANGES[1];
      const activeDays = matched.days; // null = all time
      let soQuery = supabase
        .from("sub_orders")
        .select("id, total, subtotal, status, created_at, delivered_at, completed_at, sub_number, delivery_method")
        .eq("seller_id", seller.id)
        .order("created_at", { ascending: false })
        .limit(activeDays == null ? 2000 : 1000);
      if (activeDays != null) {
        soQuery = soQuery.gte("created_at", startOfRange(activeDays).toISOString());
      }
      const [soRes, revRes, prodRes, escrowRes] = await Promise.all([
        soQuery,
        supabase
          .from("reviews")
          .select("id, rating, text, created_at, buyer:users!reviews_buyer_id_fkey(name, avatar_url)")
          .eq("seller_id", seller.id)
          .order("created_at", { ascending: false })
          .limit(5),
        supabase
          .from("products")
          .select("id", { count: "exact", head: true })
          .eq("seller_id", seller.id)
          .is("deleted_at", null)
          .eq("status", "active"),
        supabase
          .from("escrow_ledger")
          .select("seller_payout")
          .eq("seller_id", seller.id)
          .eq("status", "held"),
      ]);
      return {
        subOrders: soRes.data || [],
        reviews: revRes.data || [],
        productCount: prodRes.count || 0,
        pendingPayout: (escrowRes.data || []).reduce((s, e) => s + (e.seller_payout || 0), 0),
      };
    },
  });

  const subOrders = data?.subOrders || [];
  const reviews = data?.reviews || [];
  const productCount = data?.productCount || 0;
  const pendingPayout = data?.pendingPayout || 0;

  // Realtime: any sub_order change for this seller invalidates the dashboard
  // cache so KPIs and lists stay live without a manual refresh.
  useEffect(() => {
    if (!seller?.id) return;
    const channelName = `seller-dash:${seller.id}:${range}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "sub_orders", filter: `seller_id=eq.${seller.id}` },
        () => queryClient.invalidateQueries({ queryKey: ["seller-dash", seller.id, range] })
      )
      .subscribe(logChannelStatus(channelName));
    return () => {
      supabase.removeChannel(channel);
    };
  }, [seller?.id, range, queryClient]);

  const activeRange = RANGES.find((r) => r.id === range) || RANGES[1];

  // KPIs for the selected range window. Data is already scoped server-side to
  // the range (or all-time), so no extra client filter needed.
  //
  // Revenue = earned only (delivered + completed — money that has landed).
  // Orders  = all non-voided tickets (includes in-flight confirmed/preparing/shipped).
  // AOV     = average ticket size across all non-voided orders. Using earned-only
  //           would show K0 while orders are still in flight, which is confusing
  //           when the Orders card already shows a non-zero count.
  const kpis = useMemo(() => {
    const earned = subOrders.filter((o) => EARNING_STATUSES.has(o.status));
    const countable = subOrders.filter((o) => !VOIDED_STATUSES.has(o.status));
    const revenue = earned.reduce((s, o) => s + (o.total || 0), 0);
    const gross = countable.reduce((s, o) => s + (o.total || 0), 0);
    const orderCount = countable.length;
    const aov = orderCount > 0 ? gross / orderCount : 0;
    return { revenue, orderCount, aov, earnedCount: earned.length };
  }, [subOrders]);

  // Revenue sparkline — bucket strategy switches with the active range so
  // "All time" shows monthly bars instead of 365+ daily ones.
  const spark = useMemo(() => {
    const days = activeRange.days;
    // Strategy: daily for ≤30d, weekly for 90d, monthly for all-time, hourly for today.
    let strategy;
    if (days === 1) strategy = "hour";
    else if (days != null && days <= 30) strategy = "day";
    else if (days != null && days <= 90) strategy = "week";
    else strategy = "month";

    const bins = {};
    const keys = [];
    const now = new Date();

    if (strategy === "hour") {
      const startOfToday = new Date(now);
      startOfToday.setHours(0, 0, 0, 0);
      for (let h = 0; h < 24; h++) {
        const d = new Date(startOfToday);
        d.setHours(h);
        const k = `${dayKey(d)}-${h}`;
        bins[k] = 0;
        keys.push({ key: k, date: d, label: String(h).padStart(2, "0") });
      }
      subOrders.forEach((o) => {
        if (!EARNING_STATUSES.has(o.status)) return;
        const d = new Date(o.created_at);
        if (d < startOfToday) return;
        const k = `${dayKey(d)}-${d.getHours()}`;
        if (k in bins) bins[k] += o.total || 0;
      });
    } else if (strategy === "day") {
      const n = days;
      const anchor = new Date(now);
      anchor.setHours(0, 0, 0, 0);
      // ≤7d: single-letter weekday (S/M/T...). 8–30d: "Mar 19" — weekday
      // letters repeat across weeks and become meaningless past one week.
      const useWeekday = n <= 7;
      for (let i = n - 1; i >= 0; i--) {
        const d = new Date(anchor);
        d.setDate(d.getDate() - i);
        const k = dayKey(d);
        bins[k] = 0;
        keys.push({
          key: k,
          date: d,
          label: useWeekday
            ? d.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 1)
            : d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        });
      }
      subOrders.forEach((o) => {
        if (!EARNING_STATUSES.has(o.status)) return;
        const k = dayKey(o.created_at);
        if (k in bins) bins[k] += o.total || 0;
      });
    } else if (strategy === "week") {
      const weeks = 13;
      const anchor = new Date(now);
      anchor.setHours(0, 0, 0, 0);
      for (let i = weeks - 1; i >= 0; i--) {
        const d = new Date(anchor);
        d.setDate(d.getDate() - i * 7);
        const k = weekKey(d);
        if (!(k in bins)) {
          bins[k] = 0;
          const wDate = new Date(k);
          keys.push({
            key: k,
            date: wDate,
            label: wDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          });
        }
      }
      subOrders.forEach((o) => {
        if (!EARNING_STATUSES.has(o.status)) return;
        const k = weekKey(o.created_at);
        if (k in bins) bins[k] += o.total || 0;
      });
    } else {
      // Monthly for all-time. Derive month span from oldest order (or last 12 months).
      let oldest = now;
      subOrders.forEach((o) => {
        const d = new Date(o.created_at);
        if (d < oldest) oldest = d;
      });
      const startMonth = new Date(oldest.getFullYear(), oldest.getMonth(), 1);
      const endMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const cur = new Date(startMonth);
      while (cur <= endMonth) {
        const k = monthKey(cur);
        bins[k] = 0;
        keys.push({
          key: k,
          date: new Date(cur),
          label: cur.toLocaleDateString("en-US", { month: "short" }),
        });
        cur.setMonth(cur.getMonth() + 1);
      }
      // If we have no data at all, show last 6 months as empty scaffold.
      if (keys.length === 0) {
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const k = monthKey(d);
          bins[k] = 0;
          keys.push({ key: k, date: d, label: d.toLocaleDateString("en-US", { month: "short" }) });
        }
      }
      subOrders.forEach((o) => {
        if (!EARNING_STATUSES.has(o.status)) return;
        const k = monthKey(o.created_at);
        if (k in bins) bins[k] += o.total || 0;
      });
    }

    return keys.map(({ key, date, label }) => ({ key, date, label, value: bins[key] || 0 }));
  }, [subOrders, activeRange.days]);

  const needingAction = useMemo(
    () => subOrders.filter((o) => o.status === "confirmed").slice(0, 5),
    [subOrders]
  );

  const activity = useMemo(() => {
    const orderEvents = subOrders.slice(0, 10).map((o) => ({
      id: `o-${o.id}`,
      kind: o.status === "confirmed" ? "new_order" : "order_update",
      ts: o.created_at,
      title: o.status === "confirmed" ? `New order ${o.sub_number}` : `Order ${o.sub_number} — ${o.status}`,
      body: fmtK(o.total),
      icon: o.status === "confirmed" ? "package" : "truck",
    }));
    const reviewEvents = reviews.map((r) => ({
      id: `r-${r.id}`,
      kind: "review",
      ts: r.created_at,
      title: `${r.rating}★ review from ${r.buyer?.name || "Buyer"}`,
      body: r.text?.slice(0, 80) || "",
      icon: "star",
    }));
    return [...orderEvents, ...reviewEvents]
      .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
      .slice(0, 8);
  }, [subOrders, reviews]);

  if (!seller) {
    return (
      <div>
        <EmptyState
          icon="store"
          title="Open a store on Kapio"
          body="Sell to thousands of students with escrow buyer protection, built-in chat, and weekly payouts. Admin review takes ~1 business day."
          action={
            <Btn onClick={() => setBecomeOpen(true)} size="lg">
              Become a seller
            </Btn>
          }
        />
        <BecomeSellerModal
          open={becomeOpen}
          onClose={() => setBecomeOpen(false)}
          userId={user?.id}
          onSuccess={async () => {
            setBecomeOpen(false);
            await onSellerCreated?.();
          }}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: "grid", gap: 16 }}>
        <Skeleton height={90} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} height={120} />
          ))}
        </div>
        <Skeleton height={280} />
      </div>
    );
  }

  const maxSpark = Math.max(1, ...spark.map((d) => d.value));

  return (
    <div>
      <MfaNudgeBanner
        onSetup={() => openProfileTab?.("security")}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <div>
          <Eyebrow>Dashboard</Eyebrow>
          <h1
            style={{
              fontSize: 28,
              fontWeight: 900,
              color: C.charcoal,
              margin: 0,
              letterSpacing: "-0.6px",
            }}
          >
            {seller.store_name}
          </h1>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
            {seller.rating_avg ? `${Number(seller.rating_avg).toFixed(1)}★ · ${seller.rating_count || 0} reviews` : "No reviews yet"}
            {" · "}
            {productCount} active {productCount === 1 ? "product" : "products"}
          </div>
        </div>

        <div
          style={{
            display: "inline-flex",
            padding: 3,
            borderRadius: 12,
            background: C.cream,
            border: "1px solid rgba(196,193,183,0.3)",
            gap: 2,
          }}
        >
          {RANGES.map((r) => {
            const on = range === r.id;
            return (
              <button
                key={r.id}
                onClick={() => setRange(r.id)}
                style={{
                  border: "none",
                  background: on ? C.white : "transparent",
                  color: on ? C.hunter : C.muted,
                  fontSize: 12,
                  fontWeight: 800,
                  padding: "6px 12px",
                  borderRadius: 9,
                  cursor: "pointer",
                  boxShadow: on ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
                }}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 18 }}>
        <Kpi label="Revenue" value={fmtK(kpis.revenue)} icon="trending-up" tone="hunter" />
        <Kpi label="Orders" value={String(kpis.orderCount)} icon="package" tone="hunter" />
        <Kpi label="Avg order value" value={fmtK(kpis.aov)} icon="receipt" tone="hunter" />
        <Kpi label="Escrow (pending payout)" value={fmtK(pendingPayout)} icon="wallet" tone="amber" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 16, marginBottom: 18 }}>
        <Card pad={22}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <Eyebrow>Revenue · {activeRange.label}</Eyebrow>
              <div style={{ fontSize: 22, fontWeight: 900, color: C.charcoal, letterSpacing: "-0.4px" }}>
                {fmtK(spark.reduce((s, d) => s + d.value, 0))}
              </div>
            </div>
            <Btn kind="ghost" size="sm" onClick={() => setActive("analytics")}>
              Full analytics →
            </Btn>
          </div>
          <Sparkline data={spark} max={maxSpark} />
        </Card>

        <Card pad={22}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <Eyebrow>Needs action</Eyebrow>
            <Btn kind="ghost" size="sm" onClick={() => setActive("sorders")}>
              All orders →
            </Btn>
          </div>
          {needingAction.length === 0 ? (
            <div style={{ fontSize: 12.5, color: C.muted, padding: "12px 0" }}>
              You're all caught up. New orders will appear here when buyers pay.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {needingAction.map((o) => (
                <div
                  key={o.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 12px",
                    background: C.amberBg,
                    borderRadius: 10,
                    border: `1px solid ${C.amberBg}`,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: C.amberDk }}>{o.sub_number}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{timeAgo(o.created_at)} · {o.delivery_method}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}>{fmtK(o.total)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card pad={22}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <Eyebrow>Live activity</Eyebrow>
          <div style={{ fontSize: 11, color: C.muted }}>Last {activity.length} events</div>
        </div>
        {activity.length === 0 ? (
          <div style={{ fontSize: 12.5, color: C.muted }}>No activity yet.</div>
        ) : (
          <div style={{ display: "grid", gap: 2 }}>
            {activity.map((e) => (
              <div
                key={e.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 0",
                  borderBottom: `1px solid ${C.cream}`,
                }}
              >
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: C.cream,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon name={e.icon} size={14} color={C.hunter} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.charcoal }}>{e.title}</div>
                  {e.body && (
                    <div
                      style={{
                        fontSize: 11.5,
                        color: C.muted,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {e.body}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color: C.muted }}>{timeAgo(e.ts)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value, icon, tone }) {
  const bg = tone === "amber" ? C.amberBg : C.cream;
  const fg = tone === "amber" ? C.amberDk : C.hunter;
  return (
    <Card pad={18}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            background: bg,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name={icon} size={15} color={fg} />
        </span>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>
          {label}
        </div>
      </div>
      <div style={{ fontSize: 22, fontWeight: 900, color: C.charcoal, letterSpacing: "-0.4px" }}>{value}</div>
    </Card>
  );
}

function Sparkline({ data, max }) {
  const n = data.length;
  const [selected, setSelected] = useState(null);

  // Axis label thinning — same rules as before so long ranges don't overlap.
  const labelEvery = n <= 8 ? 1 : n <= 16 ? 2 : Math.ceil(n / 8);

  // SVG coordinates — use a fixed viewBox and let it stretch to container width.
  const W = 600;
  const H = 140;
  const padX = 12;
  const padTop = 10;
  const padBottom = 10;
  const innerW = W - padX * 2;
  const innerH = H - padTop - padBottom;

  if (n === 0) return null;

  const stepX = n > 1 ? innerW / (n - 1) : 0;
  const points = data.map((d, i) => {
    const x = padX + (n > 1 ? stepX * i : innerW / 2);
    const h = max > 0 ? (d.value / max) * innerH : 0;
    const y = padTop + (innerH - h);
    return { x, y, d, i };
  });

  // Smooth line via catmull-rom-ish cubic bezier for a softer trend curve.
  const linePath = points
    .map((p, i) => {
      if (i === 0) return `M ${p.x} ${p.y}`;
      const prev = points[i - 1];
      const cx = (prev.x + p.x) / 2;
      return `C ${cx} ${prev.y}, ${cx} ${p.y}, ${p.x} ${p.y}`;
    })
    .join(" ");

  const areaPath = `${linePath} L ${points[n - 1].x} ${padTop + innerH} L ${points[0].x} ${padTop + innerH} Z`;

  const sel = selected != null ? points[selected] : null;

  return (
    <div>
      <div style={{ position: "relative", height: H }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          style={{ width: "100%", height: "100%", display: "block", overflow: "visible" }}
        >
          <defs>
            <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={C.hunter} stopOpacity="0.25" />
              <stop offset="100%" stopColor={C.hunter} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Baseline */}
          <line
            x1={padX}
            y1={padTop + innerH}
            x2={W - padX}
            y2={padTop + innerH}
            stroke={C.dustDk}
            strokeWidth="1"
            strokeDasharray="3 4"
          />

          {/* Area under line */}
          {max > 0 && <path d={areaPath} fill="url(#sparkFill)" />}

          {/* Trend line */}
          <path
            d={linePath}
            fill="none"
            stroke={C.hunter}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Clickable data points — larger invisible hit target over visible dot */}
          {points.map((p) => {
            const isSelected = selected === p.i;
            return (
              <g key={p.d.key}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={isSelected ? 5 : 3.5}
                  fill={isSelected ? C.hunter : C.white}
                  stroke={C.hunter}
                  strokeWidth="2"
                  style={{ transition: "r .15s ease" }}
                />
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={12}
                  fill="transparent"
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelected(selected === p.i ? null : p.i)}
                >
                  <title>{`${p.d.label}: ${fmtK(p.d.value)}`}</title>
                </circle>
              </g>
            );
          })}
        </svg>

        {/* Selected-point tooltip — positioned in percent so it tracks SVG stretching */}
        {sel && (
          <div
            style={{
              position: "absolute",
              left: `${(sel.x / W) * 100}%`,
              top: `${(sel.y / H) * 100}%`,
              transform: "translate(-50%, calc(-100% - 10px))",
              background: C.charcoal,
              color: C.white,
              padding: "6px 10px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 700,
              whiteSpace: "nowrap",
              pointerEvents: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            }}
          >
            <div style={{ fontSize: 9.5, fontWeight: 600, opacity: 0.7, marginBottom: 1 }}>{sel.d.label}</div>
            <div>{fmtK(sel.d.value)}</div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        {data.map((d, i) => (
          <div
            key={d.key}
            style={{
              flex: 1,
              fontSize: 10,
              fontWeight: 700,
              color: C.muted,
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {i % labelEvery === 0 ? d.label : ""}
          </div>
        ))}
      </div>
    </div>
  );
}
